import React                     from 'react';
import View                      from '@widesk-ui/components/View';
import Tooltip                   from '@widesk-ui/components/Tooltip';
import useTheme                  from '@widesk-ui/hooks/useTheme';
import { observer }              from 'mobx-react-lite';
import { MergeCellsOutlined }    from '@ant-design/icons';
import currentEditorStore        from '@/TokamapEditor/stores/currentEditorStore';
import { mergeShapes }           from '@/TokamapEditor/helpers/mergeShapes';
import { mergeBlocker }          from '@/TokamapEditor/helpers/mergeShapes';
import useDesignTokens           from '@/hooks/useDesignTokens';
import type { Shape }            from '@tokamap/core';

/** Segment de la barre d'outils, visible seulement quand une sélection multiple peut être fusionnée. */
const MergeButton = observer(({ separator }: { separator: React.ReactNode }) => {
	const { colorPrimary, colorTextLightSolid } = useTheme();
	const tokens = useDesignTokens();
	const editor = currentEditorStore.get;
	const [shapeIds, setShapeIds] = React.useState<string[]>([]);

	// La géométrie n'est calculée qu'au clic (`mergeShapes`) : sur une grande sélection, l'union
	// bloquerait l'ouverture du panneau plusieurs secondes.
	const blocker = React.useMemo(() => {
		const shapes = shapeIds.map(id => editor?.shapeStore.get(id)).filter((shape): shape is Shape => !!shape);

		return shapes.length >= 2 ? mergeBlocker(shapes) : undefined;
	}, [editor, shapeIds]);

	React.useEffect(() => {
		if (!editor) return;

		return editor.mapAdapter.on(event => {
			if (event.type === 'selection:changed' && !event.interacting) setShapeIds(event.shapeIds);
		});
	}, [editor]);

	if (blocker === undefined) return null;

	const ok = blocker === null;

	return (
		<>
			<Tooltip title={ok ? `Fusionner les ${shapeIds.length} formes en une seule (Ctrl + J)` : blocker} placement="bottom">
				<div style={{ display: 'flex', height: '100%' }}>
					<View
						className="merge-button"
						row
						centerV
						gap="xs"
						px="sm"
						h="100%"
						pointer={ok}
						bold
						bg={ok ? colorPrimary : tokens.fill.subtle}
						color={ok ? colorTextLightSolid : tokens.text.disabled}
						style={ok ? undefined : { cursor: 'not-allowed' }}
						onClick={ok ? () => void mergeShapes(shapeIds) : undefined}
					>
						<MergeCellsOutlined />
						Fusionner
					</View>
				</div>
			</Tooltip>

			{separator}
		</>
	);
});

export default MergeButton;
